import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AiChatPanelItem } from '@app/core/services/ai-chat.types';

@Injectable({
    providedIn: 'root',
})
export class AiChatPanelService {
    private readonly openSubject = new BehaviorSubject(false);

    private readonly settingsSubject = new BehaviorSubject<AiChatPanelItem | undefined>(undefined);

    /** Reactive panel visibility shared by the downgraded navbar and panel host. */
    public readonly open$ = this.openSubject.asObservable();

    /** Latest access and remembered-layout settings retrieved from datatask 4052. */
    public readonly settings$ = this.settingsSubject.asObservable();

    /** Latest panel settings, or `undefined` until the initial request completes. */
    public get settings() {
        return this.settingsSubject.value;
    }

    /** Whether the current portal user can access AI chat. */
    public get canUseAiChat() {
        return this.settingsSubject.value?.can_use_ai_chat === '1';
    }

    /** Whether the current portal user can edit AI configuration. */
    public get canEditAiConfiguration() {
        return this.settingsSubject.value?.can_edit_ai_configuration === '1';
    }

    /** Updates the feature state once the panel settings request has completed. */
    public setSettings(settings: AiChatPanelItem) {
        this.settingsSubject.next(settings);
    }

    /** Opens the panel without persisting state; the panel host owns persistence. */
    public open() {
        this.openSubject.next(true);
    }

    /** Closes the panel without persisting state; the panel host owns persistence. */
    public close() {
        this.openSubject.next(false);
    }

    /** Toggles panel visibility without persisting state; the panel host owns persistence. */
    public toggle() {
        this.openSubject.next(!this.openSubject.value);
    }
}
